import { updateUserMutation } from '@/api/users/usersMutations';
import type { UsersFilters } from '@/api/users/usersQueries';
import { getUsersQuery } from '@/api/users/usersQueries';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { User } from '@/domain/User';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { ShowPatientsFilters } from '../show-patient/ShowPatientsFilter';
import { RestrictUserDialog } from './RestrictUserDialog';

const INITIAL_RESTRICT_FILTERS: UsersFilters = {};

export default function RestrictedUsersRow({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<UsersFilters>(
    INITIAL_RESTRICT_FILTERS,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_RESTRICT_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const { data: response, isLoading } = useQuery({
    ...getUsersQuery(page, filters),
  });

  const users = (response?.data || []) as Array<User>;
  const meta = response?.meta;

  const updateMutation = useMutation({
    ...updateUserMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDialogOpen(false);
      setSelectedUser(null);
      toast.success('User restriction status updated');
    },
  });

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleSubmit = (userId: number, restricted: boolean) => {
    updateMutation.mutate({
      id: userId,
      restricted,
    });
  };

  return (
    <>
      <DashboardTableAccordion
        value="Restrict users"
        title="Restrict users"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users found."
        loadingMessage="Loading users..."
        paginationMeta={meta}
        onPageChange={setPage}
        filtersComponent={
          <ShowPatientsFilters
            key={filtersResetKey}
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
          />
        }
        renderHeader={() => (
          <>
            <TableHead className="w-12"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Restricted</TableHead>
            <TableHead className="w-24"></TableHead>
          </>
        )}
        renderRow={(user, index) => (
          <>
            <TableCell>
              {(page - 1) * (meta?.per_page || 10) + index + 1}
            </TableCell>
            <TableCell>
              {user.name} {user.surname}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.restricted ? 'Yes' : 'No'}</TableCell>
            <TableCell className="text-right">
              <RelationActionButton
                onClick={() => handleEdit(user)}
                disabled={updateMutation.isPending}
              >
                Edit
              </RelationActionButton>
            </TableCell>
          </>
        )}
      />

      <RestrictUserDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={selectedUser}
        onSubmit={handleSubmit}
        isPending={updateMutation.isPending}
      />
    </>
  );
}
